import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, GridColumn, Icon, Label } from "semantic-ui-react";

import { AttachmentsPreview, Can, useCan } from "components";
import { QuestionResultStatusSelector } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { getRenderedInterventionPrice } from "modules/AppointmentDetails/components/CustomerCommunicationModal";
import { useAppointmentLocation, useToggleAttachmentVisibilityMutation } from "modules/AppointmentDetails/hooks";

import type { Intervention } from "models";

type CustomerCommunicationInterventionItemProps = {
  intervention: Intervention;
  toggleVisible: (item: Intervention) => void;
  showVat: boolean;
  toggleCustomerOk?: (item: Intervention) => void;
};

export const CustomerCommunicationInterventionItem = ({ intervention, toggleVisible, toggleCustomerOk, showVat }: CustomerCommunicationInterventionItemProps) => {
  const { t } = useTranslation();

  const selectedLocation = useAppointmentLocation();
  const canUpdateInterventions = useCan("update", "appointments");
  const { mutate: toggleAttachmentVisibility } = useToggleAttachmentVisibilityMutation();

  const [showMedia, setShowMedia] = useState(false);

  const attachments = [...(intervention.attachments || []), ...(intervention.diagnose_overview_attachments || [])];
  const numberOfAttachments = attachments.filter(att => att.url.match(/\.(jpeg|jpg|gif|png|mp4|mov|mpeg|mkv|webm)/i)).length;

  const toggleShowMedia = () => setShowMedia(prev => !prev);

  const handleToggleAttachmentVisibility = (visible_to_customer: boolean, intervention_attachment_id?: number) =>
    toggleAttachmentVisibility({ intervention_attachment_id, visible_to_customer });

  const renderAttachmentPreview = () => {
    return (
      attachments.length > 0 && (
        <div className="CustomerCommunicationInterventions_attachment-container">
          <AttachmentsPreview attachments={attachments} onToggleVisibility={handleToggleAttachmentVisibility} canPreviewFiles={false} isFilesVisible={false} />
        </div>
      )
    );
  };

  return (
    <div className="Intervention" key={intervention.id}>
      <Grid>
        <GridColumn width={13}>
          <div className="title-container">
            {intervention.question_result_status ? (
              <div className="question-status-container">
                <QuestionResultStatusSelector intervention={intervention} disabled />
              </div>
            ) : null}
            <div className="title-container-button">
              {!intervention.is_local && <Icon className="desktop dmsIcon" size="tiny" />}
              <p>{intervention.title}</p>
            </div>
          </div>
        </GridColumn>
        <GridColumn width={1}>
          {selectedLocation?.dms_price_enabled && (
            <div className="column-container">
              <p>{getRenderedInterventionPrice(intervention, t, showVat, selectedLocation)}</p>
            </div>
          )}
        </GridColumn>
        <GridColumn width={2}>
          <Can I="update" the="interventions">
            <div className="column-container float-right">
              <div className="paperclip-gap">
                {numberOfAttachments > 0 && (
                  <Label className="skyblue" onClick={toggleShowMedia}>
                    <div className="flex-row">
                      <Icon className="paperclip" /> {numberOfAttachments}
                    </div>
                  </Label>
                )}
              </div>
              <div className="status-container">
                <div>
                  <Icon
                    className={`eye ${intervention.visible_to_customer ? "green" : "slash red"} ${canUpdateInterventions ? "pointer" : ""}`}
                    size="small"
                    onClick={() => toggleVisible(intervention)}
                  />
                </div>
                {toggleCustomerOk && (
                  <div>
                    <Icon
                      className={`${intervention.customer_ok ? "check circle green" : "remove circle red"}`}
                      size="large"
                      onClick={() => toggleCustomerOk(intervention)}
                      disabled={!canUpdateInterventions}
                    />
                  </div>
                )}
              </div>
            </div>
          </Can>
        </GridColumn>
        {showMedia && <>{renderAttachmentPreview()}</>}
      </Grid>
    </div>
  );
};
